/**
 * Production - Página de Produção Naval (v2 - 2 níveis)
 * Nível 1: Cards de projetos com visão geral
 * Nível 2: Detalhe do projeto com abas (Produção + Diário)
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import api from '../lib/axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { TitleWithHelp } from '../components/ui/help-tooltip';
import {
  Factory, Ship, Loader2, Eye, ArrowLeft, BookOpen,
  Search, ChevronRight, Clock, CheckCircle2, AlertTriangle, Anchor,
  LayoutGrid, List, Truck
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

import {
  STATUS_CONFIG,
  ETAPAS_MACRO_PADRAO,
  calculateStatus,
  isFaseNoPeriodo,
  getFilterDates,
  calcularIndicadores,
  IndicadoresProducao,
  FiltrosPeriodo,
  ListaFases,
  DialogDetalhesFase,
  DialogConclusao
} from '../components/producao';
import DiarioProducao from '../components/producao/DiarioProducao';

// ============ NÍVEL 1: CARD DO PROJETO ============
const ProjectCard = ({ project, vesselModels, factories, cronogramas, onSelect }) => {
  const emb = project.embarcacoes?.[0];
  const modelo = emb ? vesselModels.find(m => m.id === emb.vessel_model_id) : null;
  const fabrica = factories.find(f => f.id === project.factory_id);
  const crono = cronogramas[`${project.id}_0`];
  
  const totalFases = crono?.itens?.length || 0;
  const fasesConcluidas = crono?.itens?.filter(f => f.status === 'concluido')?.length || 0;
  const fasesAndamento = crono?.itens?.filter(f => f.status === 'em_andamento')?.length || 0;
  const fasesAtrasadas = crono?.itens?.filter(f => {
    if (f.status === 'concluido') return false;
    const deadline = f.data_previsao_fim || f.data_fim;
    return deadline && new Date(deadline) < new Date();
  })?.length || 0;
  const progresso = totalFases > 0 ? Math.round((fasesConcluidas / totalFases) * 100) : 0;

  const getStatusInfo = () => {
    if (totalFases === 0) return { label: 'Sem fases', color: 'text-gray-400', bg: 'bg-gray-50' };
    if (fasesConcluidas === totalFases) return { label: 'Concluído', color: 'text-green-600', bg: 'bg-green-50' };
    if (fasesAtrasadas > 0) return { label: 'Atrasado', color: 'text-red-600', bg: 'bg-red-50' };
    if (fasesAndamento > 0) return { label: 'Em andamento', color: 'text-blue-600', bg: 'bg-blue-50' };
    return { label: 'Pendente', color: 'text-amber-600', bg: 'bg-amber-50' };
  };
  const statusInfo = getStatusInfo();

  return (
    <Card
      className="cursor-pointer hover:shadow-lg hover:border-blue-300 transition-all group"
      onClick={() => onSelect(project)}
      data-testid={`production-card-${project.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 truncate group-hover:text-blue-700 transition-colors">
              {project.name}
            </h3>
            <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
              {modelo && <span className="flex items-center gap-1"><Ship className="h-3 w-3" />{modelo.nome}</span>}
              {fabrica && <span className="flex items-center gap-1"><Factory className="h-3 w-3" />{fabrica.name}</span>}
            </div>
          </div>
          <Badge className={`text-xs shrink-0 ${statusInfo.bg} ${statusInfo.color} border-0`}>
            {statusInfo.label}
          </Badge>
        </div>

        {/* Progresso */}
        <div className="mb-3">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>{fasesConcluidas}/{totalFases} fases</span>
            <span className="font-medium">{progresso}%</span>
          </div>
          <Progress value={progresso} className="h-2" />
        </div>

        {/* Indicadores rápidos */}
        <div className="flex gap-3 text-xs">
          {fasesAndamento > 0 && (
            <span className="flex items-center gap-1 text-blue-600">
              <Clock className="h-3 w-3" />{fasesAndamento} em andamento
            </span>
          )}
          {fasesAtrasadas > 0 && (
            <span className="flex items-center gap-1 text-red-600">
              <AlertTriangle className="h-3 w-3" />{fasesAtrasadas} atrasada(s)
            </span>
          )}
          {fasesConcluidas === totalFases && totalFases > 0 && (
            <span className="flex items-center gap-1 text-green-600">
              <CheckCircle2 className="h-3 w-3" />Completo
            </span>
          )}
          <span className="flex items-center gap-1 text-gray-400 ml-auto">
            {project.embarcacoes?.length || 0} emb.
            <ChevronRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

// ============ COMPONENTE PRINCIPAL ============
const Production = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [factories, setFactories] = useState([]);
  const [vesselModels, setVesselModels] = useState([]);
  const [etapasMacro, setEtapasMacro] = useState([]);
  const [cronogramas, setCronogramas] = useState({});

  // Nível 2: Projeto selecionado
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedEmbarcacao, setSelectedEmbarcacao] = useState(0);
  const [cronograma, setCronograma] = useState(null);
  const [activeTab, setActiveTab] = useState('producao');
  const [selectedFactoryFilter, setSelectedFactoryFilter] = useState('todas');

  // Filtro de período
  const [viewFilter, setViewFilter] = useState('mensal');
  const [customDateStart, setCustomDateStart] = useState('');
  const [customDateEnd, setCustomDateEnd] = useState('');
  const [filterStartDate, setFilterStartDate] = useState(new Date());
  const [filterEndDate, setFilterEndDate] = useState(new Date());

  // Diálogo de fase
  const [showFaseDialog, setShowFaseDialog] = useState(false);
  const [selectedFase, setSelectedFase] = useState(null);
  const [savingFase, setSavingFase] = useState(false);

  // Fases expandidas
  const [expandedFases, setExpandedFases] = useState({});

  // Modal de conclusão de fase
  const [showConclusaoDialog, setShowConclusaoDialog] = useState(false);
  const [faseConcluindo, setFaseConcluindo] = useState(null);
  const [fotoConclusao, setFotoConclusao] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [observacaoConclusao, setObservacaoConclusao] = useState('');
  const [salvandoConclusao, setSalvandoConclusao] = useState(false);

  // Filtro de cards
  const [searchTerm, setSearchTerm] = useState('');
  const [filterFactory, setFilterFactory] = useState('todas');
  const [filterClient, setFilterClient] = useState('todos');
  const [filterStatus, setFilterStatus] = useState('todos');
  const [filterModel, setFilterModel] = useState('todos');
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    document.title = "Produção — NautilusOS";
    fetchData();
    updateFilterDates('mensal');
  }, []);

  useEffect(() => {
    if (selectedProject && selectedEmbarcacao !== null) {
      fetchCronograma();
    }
  }, [selectedProject, selectedEmbarcacao]);

  useEffect(() => {
    if (viewFilter !== 'personalizado') updateFilterDates(viewFilter);
  }, [viewFilter]);

  const updateFilterDates = (filter) => {
    const { start, end } = getFilterDates(filter);
    setFilterStartDate(start);
    setFilterEndDate(end);
  };

  const applyCustomFilter = () => {
    if (customDateStart && customDateEnd) {
      setFilterStartDate(new Date(customDateStart));
      setFilterEndDate(new Date(customDateEnd));
    }
  };

  const fetchData = async () => {
    try {
      const userStr = localStorage.getItem('nautilus_user');
      const user = userStr ? JSON.parse(userStr) : null;
      setCurrentUser(user);

      const [projectsRes, factoriesRes, modelsRes] = await Promise.all([
        api.get('/projects'),
        api.get('/factories'),
        api.get('/vessel-models')
      ]);

      const projs = projectsRes.data || [];
      setProjects(projs);
      setFactories(factoriesRes.data || []);
      setVesselModels(modelsRes.data || []);

      try {
        const etapasMacroRes = await api.get('/etapas-macro');
        setEtapasMacro(etapasMacroRes.data || []);
      } catch (err) {
        console.error('Etapas macro fallback:', err);
        setEtapasMacro(ETAPAS_MACRO_PADRAO);
      }

      // Carregar cronogramas de todos os projetos para os cards
      const cronoMap = {};
      await Promise.all(
        projs.slice(0, 50).map(async (p) => {
          try {
            const res = await api.get(`/cronogramas/${p.id}/0`);
            cronoMap[`${p.id}_0`] = res.data;
          } catch (err) { console.error(`Cronograma ${p.id}:`, err.message); }
        })
      );
      setCronogramas(cronoMap);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const fetchCronograma = async () => {
    if (!selectedProject) return;
    try {
      const response = await api.get(`/cronogramas/${selectedProject.id}/${selectedEmbarcacao}`);
      setCronograma(response.data);
    } catch (err) {
      console.error('Erro ao carregar cronograma:', err);
      setCronograma(null);
    }
  };

  // Filtrar fases
  const fasesFiltradas = useMemo(() => {
    if (!cronograma?.itens) return [];
    const acessoTodas = currentUser?.acesso_todas_fabricas !== false;
    const userFabricasIds = currentUser?.fabricas_ids || [];
    return cronograma.itens.filter(fase => {
      if (selectedFactoryFilter !== 'todas' && fase.fabrica_id && fase.fabrica_id !== selectedFactoryFilter) return false;
      if (!acessoTodas && fase.fabrica_id && !userFabricasIds.includes(fase.fabrica_id)) return false;
      return isFaseNoPeriodo(fase, filterStartDate, filterEndDate);
    });
  }, [cronograma, currentUser, filterStartDate, filterEndDate, selectedFactoryFilter]);

  const indicadores = useMemo(() => calcularIndicadores(fasesFiltradas), [fasesFiltradas]);

  const modeloAtual = useMemo(() => {
    if (!selectedProject?.embarcacoes?.[selectedEmbarcacao]) return null;
    const emb = selectedProject.embarcacoes[selectedEmbarcacao];
    return vesselModels.find(m => m.id === emb.vessel_model_id);
  }, [selectedProject, selectedEmbarcacao, vesselModels]);

  // Filtrar projetos para os cards
  const filteredProjects = useMemo(() => {
    return projects.filter(p => {
      const matchSearch = !searchTerm || p.name?.toLowerCase().includes(searchTerm.toLowerCase()) || p.client?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchFactory = filterFactory === 'todas' || p.factory_id === filterFactory;
      const matchClient = filterClient === 'todos' || p.client === filterClient;
      const matchModel = filterModel === 'todos' || p.embarcacoes?.some(e => e.vessel_model_id === filterModel);
      if (filterStatus === 'todos') return matchSearch && matchFactory && matchClient && matchModel;
      const crono = cronogramas[`${p.id}_0`];
      const totalF = crono?.itens?.length || 0;
      const conclF = crono?.itens?.filter(f => f.status === 'concluido')?.length || 0;
      const atrasF = crono?.itens?.filter(f => {
        if (f.status === 'concluido') return false;
        const d = f.data_previsao_fim || f.data_fim;
        return d && new Date(d) < new Date();
      })?.length || 0;
      const andamF = crono?.itens?.filter(f => f.status === 'em_andamento')?.length || 0;
      let matchSt = false;
      if (filterStatus === 'concluido') matchSt = totalF > 0 && conclF === totalF;
      else if (filterStatus === 'em_producao') matchSt = andamF > 0;
      else if (filterStatus === 'atrasado') matchSt = atrasF > 0;
      else if (filterStatus === 'pendente') matchSt = totalF > 0 && andamF === 0 && conclF < totalF;
      else if (filterStatus === 'sem_fases') matchSt = totalF === 0;
      return matchSearch && matchFactory && matchClient && matchModel && matchSt;
    });
  }, [projects, searchTerm, filterFactory, filterClient, filterStatus, filterModel, cronogramas]);

  // Dashboard stats
  const dashStats = useMemo(() => {
    let total = projects.length, emProducao = 0, emAtraso = 0, entregas30 = 0;
    const now = new Date();
    const d30 = new Date(now.getTime() + 30 * 86400000);
    projects.forEach(p => {
      const crono = cronogramas[`${p.id}_0`];
      if (!crono?.itens?.length) return;
      const hasAndamento = crono.itens.some(f => f.status === 'em_andamento');
      const hasAtraso = crono.itens.some(f => {
        if (f.status === 'concluido') return false;
        const d = f.data_previsao_fim || f.data_fim;
        return d && new Date(d) < now;
      });
      if (hasAndamento) emProducao++;
      if (hasAtraso) emAtraso++;
      if (p.delivery_date) {
        const dd = new Date(p.delivery_date);
        if (dd >= now && dd <= d30) entregas30++;
      }
    });
    return { total, emProducao, emAtraso, entregas30 };
  }, [projects, cronogramas]);

  // Listas para filtros
  const clientsList = useMemo(() => [...new Set(projects.map(p => p.client).filter(Boolean))].sort(), [projects]);
  const modelsList = useMemo(() => {
    const ids = new Set();
    projects.forEach(p => p.embarcacoes?.forEach(e => { if (e.vessel_model_id) ids.add(e.vessel_model_id); }));
    return vesselModels.filter(m => ids.has(m.id));
  }, [projects, vesselModels]);

  // Ações de fase
  const toggleFaseExpand = (faseId) => setExpandedFases(prev => ({ ...prev, [faseId]: !prev[faseId] }));

  const iniciarFase = async (fase) => {
    if (!cronograma) return;
    try {
      const itensAtualizados = cronograma.itens.map(item =>
        item.id === fase.id ? { ...item, status: 'em_andamento', data_inicio_real: new Date().toISOString() } : item
      );
      await api.put(`/cronogramas/${selectedProject.id}/${selectedEmbarcacao}`, { ...cronograma, itens: itensAtualizados });
      setCronograma({ ...cronograma, itens: itensAtualizados });
      toast.success(`Fase "${fase.fase_nome}" iniciada!`);
    } catch (e) { console.error('[Producao] iniciarFase', e); toast.error(e?.response?.data?.detail || 'Erro ao iniciar fase'); }
  };

  const abrirConclusaoFase = (fase) => {
    setFaseConcluindo(fase);
    setFotoConclusao(null);
    setFotoPreview(null);
    setObservacaoConclusao('');
    setShowConclusaoDialog(true);
  };

  const confirmarConclusaoFase = async () => {
    if (!faseConcluindo || !cronograma) return;
    setSalvandoConclusao(true);
    try {
      let fotoUrl = null;
      if (fotoConclusao) {
        const formData = new FormData();
        formData.append('file', fotoConclusao);
        const params = new URLSearchParams({
          modulo: 'producao',
          entidade_id: selectedProject.id,
          categoria: 'conclusao_fase',
        });
        const uploadRes = await api.post(`/upload/image?${params.toString()}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        fotoUrl = uploadRes.data?.url || uploadRes.data?.image_url;
      }
      const itensAtualizados = cronograma.itens.map(item => {
        if (item.id === faseConcluindo.id) {
          return {
            ...item, status: 'concluido', progresso: 100,
            data_fim_real: new Date().toISOString(),
            foto_conclusao: fotoUrl,
            observacao_conclusao: observacaoConclusao,
            etapas: item.etapas?.map(e => ({ ...e, concluida: true }))
          };
        }
        return item;
      });
      await api.put(`/cronogramas/${selectedProject.id}/${selectedEmbarcacao}`, { ...cronograma, itens: itensAtualizados });
      setCronograma({ ...cronograma, itens: itensAtualizados });
      setShowConclusaoDialog(false);
      toast.success(`Fase "${faseConcluindo.fase_nome}" concluída!`);
    } catch (e) { console.error('[Producao] concluirFase', e); toast.error(e?.response?.data?.detail || 'Erro ao concluir fase'); }
    finally { setSalvandoConclusao(false); }
  };

  const verDetalhesFase = (fase) => { setSelectedFase({ ...fase }); setShowFaseDialog(true); };

  const salvarDetalhesFase = async () => {
    if (!selectedFase || !cronograma) return;
    setSavingFase(true);
    try {
      const itensAtualizados = cronograma.itens.map(item => item.id === selectedFase.id ? selectedFase : item);
      await api.put(`/cronogramas/${selectedProject.id}/${selectedEmbarcacao}`, { ...cronograma, itens: itensAtualizados });
      setCronograma({ ...cronograma, itens: itensAtualizados });
      setShowFaseDialog(false);
      toast.success('Fase atualizada!');
    } catch (e) { console.error('[Producao] salvarDetalhesFase', e); toast.error(e?.response?.data?.detail || 'Erro ao salvar'); }
    finally { setSavingFase(false); }
  };

  const toggleEtapaFase = async (fase, etapaIdx) => {
    if (!cronograma) return;
    try {
      const itensAtualizados = cronograma.itens.map(item => {
        if (item.id === fase.id && item.etapas) {
          const etapasAtualizadas = [...item.etapas];
          etapasAtualizadas[etapaIdx] = { ...etapasAtualizadas[etapaIdx], concluida: !etapasAtualizadas[etapaIdx].concluida };
          const concluidas = etapasAtualizadas.filter(e => e.concluida).length;
          const progresso = Math.round((concluidas / etapasAtualizadas.length) * 100);
          return { ...item, etapas: etapasAtualizadas, progresso };
        }
        return item;
      });
      await api.put(`/cronogramas/${selectedProject.id}/${selectedEmbarcacao}`, { ...cronograma, itens: itensAtualizados });
      setCronograma({ ...cronograma, itens: itensAtualizados });
    } catch (e) { console.error('[Producao] atualizarEtapa', e); toast.error(e?.response?.data?.detail || 'Erro ao atualizar etapa'); }
  };

  const handleSelectProject = (project) => {
    setSelectedProject(project);
    setSelectedEmbarcacao(0);
    setActiveTab('producao');
  };

  // Loading
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  // ============ NÍVEL 2: DETALHE DO PROJETO ============
  if (selectedProject) {
    // Progresso geral baseado em TODAS as fases (não filtradas por período)
    const todasFases = cronograma?.itens || [];
    const totalFasesGeral = todasFases.length;
    const concluidasGeral = todasFases.filter(f => f.status === 'concluido').length;
    const progressoGeral = totalFasesGeral > 0 ? Math.round((concluidasGeral / totalFasesGeral) * 100) : 0;

    return (
      <div className="p-4 md:p-6 space-y-5" data-testid="production-detail">
        {/* Header com voltar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setSelectedProject(null)} data-testid="production-back-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> Voltar
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{selectedProject.name}</h1>
              <div className="flex items-center gap-3 text-sm text-gray-500">
                {modeloAtual && <span className="flex items-center gap-1"><Ship className="h-3.5 w-3.5" />{modeloAtual.nome} ({modeloAtual.codigo})</span>}
                <span className="flex items-center gap-1 font-medium text-blue-600">{progressoGeral}% concluído</span>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (selectedProject?.embarcacoes?.length > 0) navigate(`/projects/${selectedProject.id}/embarcacao/${selectedEmbarcacao}/pdf`);
              }}
              disabled={!selectedProject?.embarcacoes?.length}
              data-testid="btn-ficha-tecnica"
            >
              <Eye className="h-4 w-4 mr-1" /> Ficha Técnica
            </Button>
            <Button
              size="sm"
              className="bg-amber-600 hover:bg-amber-700"
              onClick={() => setActiveTab('diario')}
              data-testid="btn-abrir-diario"
            >
              <BookOpen className="h-4 w-4 mr-1" /> Diário
            </Button>
          </div>
        </div>

        {/* Seletor de embarcação + fábrica */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Embarcação</Label>
            <Select value={selectedEmbarcacao.toString()} onValueChange={(v) => setSelectedEmbarcacao(parseInt(v))} disabled={!selectedProject?.embarcacoes?.length}>
              <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>
                {selectedProject?.embarcacoes?.map((emb, idx) => (
                  <SelectItem key={emb.vessel_model_id || idx} value={idx.toString()}>#{(idx + 1).toString().padStart(2, '0')} - {emb.vessel_model_nome || 'Sem modelo'}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Filtrar Fábrica</Label>
            <Select value={selectedFactoryFilter} onValueChange={setSelectedFactoryFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas as Fábricas</SelectItem>
                {factories.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Código/SKU</Label>
            <div className="h-9 px-3 py-1.5 border rounded-md bg-gray-50 text-sm flex items-center gap-2">
              <Anchor className="h-3.5 w-3.5 text-blue-600" />
              {modeloAtual?.codigo || 'Não definido'}
            </div>
          </div>
        </div>

        {/* Abas */}
        <div className="flex gap-1 border-b">
          <button
            onClick={() => setActiveTab('producao')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'producao' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            data-testid="tab-producao"
          >
            <Factory className="h-4 w-4 inline mr-1.5" />Produção
          </button>
          <button
            onClick={() => setActiveTab('diario')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'diario' ? 'border-amber-600 text-amber-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            data-testid="tab-diario"
          >
            <BookOpen className="h-4 w-4 inline mr-1.5" />Diário
          </button>
        </div>

        {/* Conteúdo da aba */}
        {activeTab === 'producao' ? (
          <div className="space-y-5">
            <IndicadoresProducao indicadores={indicadores} />
            <FiltrosPeriodo
              viewFilter={viewFilter} setViewFilter={setViewFilter}
              customDateStart={customDateStart} setCustomDateStart={setCustomDateStart}
              customDateEnd={customDateEnd} setCustomDateEnd={setCustomDateEnd}
              onApplyCustomFilter={applyCustomFilter}
              filterStartDate={filterStartDate} filterEndDate={filterEndDate}
            />
            <ListaFases
              cronograma={cronograma} fasesFiltradas={fasesFiltradas}
              etapasMacro={etapasMacro} factories={factories}
              expandedFases={expandedFases}
              onToggleFaseExpand={toggleFaseExpand} onIniciarFase={iniciarFase}
              onConcluirFase={abrirConclusaoFase} onVerDetalhes={verDetalhesFase}
              onToggleEtapa={toggleEtapaFase}
            />
          </div>
        ) : (
          <DiarioProducao
            projetoId={selectedProject.id}
            embarcacaoIndex={selectedEmbarcacao}
            currentUser={currentUser}
          />
        )}

        {/* Dialogs */}
        <DialogDetalhesFase open={showFaseDialog} onClose={() => setShowFaseDialog(false)} selectedFase={selectedFase} setSelectedFase={setSelectedFase} saving={savingFase} onSave={salvarDetalhesFase} />
        <DialogConclusao open={showConclusaoDialog} onClose={() => setShowConclusaoDialog(false)} faseConcluindo={faseConcluindo} fotoConclusao={fotoConclusao} setFotoConclusao={setFotoConclusao} fotoPreview={fotoPreview} setFotoPreview={setFotoPreview} observacaoConclusao={observacaoConclusao} setObservacaoConclusao={setObservacaoConclusao} saving={salvandoConclusao} onConfirmar={confirmarConclusaoFase} />
      </div>
    );
  }

  // ============ NÍVEL 1: LISTA DE PROJETOS ============
  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="production-page">
      {/* Header */}
      <div>
        <TitleWithHelp
          title="Produção Naval"
          description="Acompanhe a produção de todas as embarcações. Selecione um projeto para ver fases, progresso e diário de produção."
          connections={['Cronograma', 'Projetos', 'Especificações']}
        >
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Factory className="h-6 w-6 text-blue-600" />
            Produção
          </h1>
        </TitleWithHelp>
      </div>

      {/* Dashboard KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-blue-50 border-blue-100 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilterStatus('todos')}>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-blue-600 font-medium uppercase tracking-wide">Total de Projetos</p>
              <p className="text-3xl font-bold text-blue-700 mt-1">{dashStats.total}</p>
            </div>
            <Ship className="h-10 w-10 text-blue-300" />
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-100 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilterStatus('em_producao')}>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-amber-600 font-medium uppercase tracking-wide">Em Produção</p>
              <p className="text-3xl font-bold text-amber-700 mt-1">{dashStats.emProducao}</p>
            </div>
            <Clock className="h-10 w-10 text-amber-300" />
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-100 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilterStatus('atrasado')}>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-red-600 font-medium uppercase tracking-wide">Em Atraso</p>
              <p className="text-3xl font-bold text-red-700 mt-1">{dashStats.emAtraso}</p>
            </div>
            <AlertTriangle className="h-10 w-10 text-red-300" />
          </CardContent>
        </Card>
        <Card className="bg-gray-50 border-gray-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilterStatus('todos')}>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Entregas em 30 dias</p>
              <p className="text-3xl font-bold text-gray-700 mt-1">{dashStats.entregas30}</p>
            </div>
            <Truck className="h-10 w-10 text-gray-300" />
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input placeholder="Buscar projeto ou cliente..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" data-testid="production-search" />
        </div>
        <Select value={filterClient} onValueChange={setFilterClient}>
          <SelectTrigger className="w-[150px]"><SelectValue placeholder="Clientes" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos Clientes</SelectItem>
            {clientsList.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[150px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos Status</SelectItem>
            <SelectItem value="em_producao">Em Produção</SelectItem>
            <SelectItem value="atrasado">Em Atraso</SelectItem>
            <SelectItem value="concluido">Concluído</SelectItem>
            <SelectItem value="pendente">Pendente</SelectItem>
            <SelectItem value="sem_fases">Sem Fases</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterModel} onValueChange={setFilterModel}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="Modelos" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Modelos</SelectItem>
            {modelsList.map(m => <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex border rounded-md overflow-hidden ml-auto">
          <button onClick={() => setViewMode('grid')} className={`px-2.5 py-1.5 ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`} data-testid="view-grid">
            <LayoutGrid className="h-4 w-4" />
          </button>
          <button onClick={() => setViewMode('list')} className={`px-2.5 py-1.5 ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`} data-testid="view-list">
            <List className="h-4 w-4" />
          </button>
        </div>
        <span className="text-sm text-gray-400">{filteredProjects.length} projeto(s)</span>
      </div>

      {/* Grid ou Lista de Cards */}
      {filteredProjects.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Factory className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p>Nenhum projeto encontrado</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProjects.map(project => (
            <ProjectCard key={project.id} project={project} vesselModels={vesselModels} factories={factories} cronogramas={cronogramas} onSelect={handleSelectProject} />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredProjects.map(project => {
            const crono = cronogramas[`${project.id}_0`];
            const totalF = crono?.itens?.length || 0;
            const conclF = crono?.itens?.filter(f => f.status === 'concluido')?.length || 0;
            const prog = totalF > 0 ? Math.round((conclF / totalF) * 100) : 0;
            const emb = project.embarcacoes?.[0];
            const modelo = emb ? vesselModels.find(m => m.id === emb.vessel_model_id) : null;
            return (
              <div key={project.id} onClick={() => handleSelectProject(project)} className="flex items-center gap-4 p-3 bg-white border rounded-lg hover:shadow-md hover:border-blue-300 cursor-pointer transition-all" data-testid={`production-row-${project.id}`}>
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-gray-900 truncate block">{project.name}</span>
                  <span className="text-xs text-gray-500">{project.client} {modelo ? `| ${modelo.nome}` : ''}</span>
                </div>
                <div className="w-32 hidden md:block">
                  <div className="flex justify-between text-xs text-gray-500 mb-0.5"><span>{conclF}/{totalF}</span><span>{prog}%</span></div>
                  <Progress value={prog} className="h-1.5" />
                </div>
                <span className="text-xs text-gray-400">{project.embarcacoes?.length || 0} emb.</span>
                <ChevronRight className="h-4 w-4 text-gray-300" />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Production;
